/**
 * One tab per browser leads: it plays the ringtone and shows system
 * notifications, so a call rings once however many tabs are open (WEB-011,
 * research.md §5). The leader holds a Web Lock for as long as it is open;
 * when it closes, the browser grants the lock to the next tab in line.
 */
export interface LockManagerLike {
  request<T>(
    name: string,
    options: { mode: 'exclusive'; signal?: AbortSignal },
    callback: (lock: { name: string; mode: string } | null) => Promise<T> | T,
  ): Promise<T>;
}

export interface Leadership {
  isLeader(): boolean;
  /** Give up (or stop waiting for) leadership, e.g. when this tab signs out. */
  release(): void;
}

const LOCK_NAME = 'kindra-leader';

export function electLeader({
  locks = typeof navigator !== 'undefined'
    ? (navigator as { locks?: LockManagerLike }).locks
    : undefined,
  onChange,
}: { locks?: LockManagerLike; onChange?: (leader: boolean) => void } = {}): Leadership {
  if (!locks) {
    // Without Web Locks every tab rings: louder, but never silent.
    return { isLeader: () => true, release: () => undefined };
  }

  let leader = false;
  let releaseLock: (() => void) | undefined;
  const abort = new AbortController();

  locks
    .request(LOCK_NAME, { mode: 'exclusive', signal: abort.signal }, () => {
      leader = true;
      onChange?.(true);
      return new Promise<void>((resolve) => {
        releaseLock = resolve;
      });
    })
    .catch(() => undefined); // aborted while waiting

  return {
    isLeader: () => leader,
    release: () => {
      if (leader) {
        leader = false;
        releaseLock?.();
      } else {
        abort.abort();
      }
    },
  };
}
